import { PureComponent, type ReactElement } from "react";
import { connect } from "react-redux";
import { type InjectedFormProps, SubmissionError, reduxForm } from "redux-form";

import {
  type CoordinateList,
  type TrackCoordinateConfiguration,
  type TrackCoordinateOrderedCoordinateList,
} from "../../../graphql.js";
import {
  type ISortableTrack,
  loadCoordinateLists,
  setGenomeBrowserCoordinateTrackCustomization,
} from "../../../redux/modules/genome-browser.js";
import { type IAppState } from "../../../redux/reducer.js";
import { DEFAULT_COORDINATE_COLOR, TRACK_TYPE } from "../../../settings/settings.js";
import { Dialog } from "../../core/overlays/Dialogs.js";
import type { CoordinateListSelection } from "@/ui-modules/genome-viewer/coordinate-track/TrackCoordinateListsSelection.tsx";
import { TrackCoordinateListSelection } from "@/ui-modules/genome-viewer/coordinate-track/TrackCoordinateListsSelection.tsx";

export interface ICoordinateTrackCustomizationDialogOwnProps {
  onClose: () => void;
}

interface ICoordinateTrackCustomizationDialogAppStateProps {
  initialValues: Partial<FormData>;
  initiallySelectedCoordinateLists: TrackCoordinateOrderedCoordinateList[];
}

interface ICoordinateTrackCustomizationDialogDispatchProps {
  applyCoordinateConfig: (config: Partial<TrackCoordinateConfiguration>) => void;
  getCoordinateListsById: (coordinateListIds: number[]) => Promise<CoordinateList[]>;
}

export interface ICoordinateTrackCustomizationDialogProps
  extends ICoordinateTrackCustomizationDialogOwnProps,
    ICoordinateTrackCustomizationDialogAppStateProps,
    ICoordinateTrackCustomizationDialogDispatchProps,
    InjectedFormProps<FormData, any> {}

function mapStateToProps(state: IAppState): ICoordinateTrackCustomizationDialogAppStateProps {
  const track: ISortableTrack = state.genomeBrowser.tracks.find(
    (tr: ISortableTrack) => tr.type === TRACK_TYPE.COORDINATE,
  );
  const coordinateConfiguration: TrackCoordinateConfiguration = track?.trackCustomization?.coordinateConfiguration;
  return {
    initialValues: {},
    initiallySelectedCoordinateLists: coordinateConfiguration?.orderedCoordinateLists || [],
  };
}

function mapDispatchToProps(dispatch): ICoordinateTrackCustomizationDialogDispatchProps {
  return {
    applyCoordinateConfig: (config: Partial<TrackCoordinateConfiguration>) =>
      dispatch(setGenomeBrowserCoordinateTrackCustomization(config)),
    getCoordinateListsById: (coordinateListIds: number[]) => dispatch(loadCoordinateLists(coordinateListIds)),
  };
}

export interface ICoordinateTrackCustomizationDialogState {
  selectedCoordinateLists: CoordinateListSelection[];
}

class CoordinatesTrackCustomizationDialog extends PureComponent<
  ICoordinateTrackCustomizationDialogProps,
  ICoordinateTrackCustomizationDialogState
> {
  public static FORM_NAME = "CoordinateTrackCustomization";

  constructor(props) {
    super(props);

    this.state = {
      selectedCoordinateLists: [],
    };
  }

  componentDidMount() {
    const selectedCoordinateLists: TrackCoordinateOrderedCoordinateList[] = this.props.initiallySelectedCoordinateLists;
    const listIds: number[] = selectedCoordinateLists.map((list) => list.coordinateListId);
    void this.props.getCoordinateListsById(listIds).then((coordinateLists: CoordinateList[]) => {
      const result: CoordinateListSelection[] = coordinateLists.map((list: CoordinateList) => {
        return {
          coordinateList: list,
          color: selectedCoordinateLists.find((s) => s.coordinateListId === list.id)?.color,
        } satisfies CoordinateListSelection;
      });
      this.setState({ selectedCoordinateLists: result });
    });
  }

  handleApply = () => {
    const configPayload: Partial<TrackCoordinateConfiguration> = {
      orderedCoordinateLists: this.state.selectedCoordinateLists.map((selectedCoordinateList) => {
        const initialConfig = this.props.initiallySelectedCoordinateLists.find(
          (coordinateList) => coordinateList.coordinateListId === selectedCoordinateList.coordinateList.id,
        );
        return {
          ...DEFAULT_COORDINATE_TRACK_CONFIGURATION,
          coordinateListId: selectedCoordinateList.coordinateList.id,
          ...(initialConfig ?? {}), // keep intact the rest of the configuration (visiblePileup)
          color: selectedCoordinateList.color,
        } satisfies TrackCoordinateOrderedCoordinateList;
      }),
    };

    try {
      this.props.applyCoordinateConfig(configPayload);
    } catch (e) {
      throw new SubmissionError({ _error: e.message });
    }
    this.props.onClose();
  };

  render(): ReactElement {
    return (
      <Dialog
        headerProps={{ title: "Coordinate Track" }}
        mainActionButtonProps={{
          label: "Apply",
          onPress: this.props.handleSubmit(this.handleApply),
        }}
        footerMessage={{ message: this.props.error }}
        closeFn={this.props.onClose}
      >
        <TrackCoordinateListSelection
          selectedCoordinateLists={this.state.selectedCoordinateLists}
          setSelectedCoordinateLists={(coordinateLists: CoordinateListSelection[]) =>
            this.setState({ selectedCoordinateLists: coordinateLists })
          }
          defaultColor={DEFAULT_COORDINATE_COLOR}
        />
      </Dialog>
    );
  }
}

export default connect<
  ICoordinateTrackCustomizationDialogAppStateProps,
  ICoordinateTrackCustomizationDialogDispatchProps,
  ICoordinateTrackCustomizationDialogOwnProps
>(
  mapStateToProps,
  mapDispatchToProps,
)(
  reduxForm({
    form: CoordinatesTrackCustomizationDialog.FORM_NAME,
  })(CoordinatesTrackCustomizationDialog as any),
);

export const DEFAULT_COORDINATE_TRACK_CONFIGURATION: TrackCoordinateOrderedCoordinateList = {
  coordinateListId: null,
  color: DEFAULT_COORDINATE_COLOR,
  collapsed: false,
};
